import { createRoute, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { menuLayoutRoute } from 'root/MenuLayout';
import RequireWarehouse from 'root/RequireWarehouse';

import StockCountEnabledRoute from './StockCountEnabledRoute';

const stockCountMainRoute = createRoute({
  id: 'stock-count-main',
  getParentRoute: () => menuLayoutRoute,
});

const StockCountRequireWarehouse = () => {
  return (
    <RequireWarehouse
      routeWithWarehouseId="/stock-count/warehouse/$warehouseId"
      routeWithoutWarehouseId="/stock-count"
    >
      <Outlet />
    </RequireWarehouse>
  );
};

const requireWarehouseRoute = createRoute({
  getParentRoute: () => stockCountMainRoute,
  path: '/stock-count',
  component: StockCountRequireWarehouse,
});

const stockCountEnabledRoute = createRoute({
  id: 'stock-count-enabled',
  getParentRoute: () => requireWarehouseRoute,
  component: StockCountEnabledRoute,
});

export const stockCountWarehouseWrapperRoute = createRoute({
  getParentRoute: () => stockCountEnabledRoute,
  path: 'warehouse/$warehouseId',
  parseParams: z.object({ warehouseId: z.string().uuid() }).parse,
  validateSearch: z.object({
    showProcedureInfo: z.boolean().optional(),
    showDrawerBinSearch: z.boolean().optional(),
    showItemSearchDrawer: z.boolean().optional(),
    showNoItemsInBinModal: z.boolean().optional(),
    showRecentlyCounted: z.boolean().optional(),
    gtin: z.string().optional(),
    q: z.coerce.string().optional(),
    recentlyCountedBy: z.enum(['countByItem', 'countByLocation']).optional(),
    searchStockCountHistoryQuery: z.string().optional(),
  }).parse,
});

export const stockCountViewRoute = createRoute({
  getParentRoute: () => stockCountWarehouseWrapperRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./StockCountView/StockCountView')),
});

/**
 * This route (and all sub-routes) is used for the "count by location" flow.
 * The "count by item" flow is handled by the /inventory-adjustments view.
 *
 * This particular route is used to fetch a sessionId from the API, which
 * is used by sub-routes.
 **/
const countByLocationRoute = createRoute({
  getParentRoute: () => stockCountWarehouseWrapperRoute,
  path: '$binLocationName',
  component: lazyRouteComponent(() => import('./CountSession/CountSessionContainer')),
  parseParams: (params) => ({
    binLocationName: z.string().parse(params.binLocationName),
  }),
});

/**
 * Once the parent route has fetched a sessionId from the backend, this route exists to put
 * the sessionId into the URL.
 **/
const countByLocationSessionRoute = createRoute({
  getParentRoute: () => countByLocationRoute,
  path: 'count/$sessionId',
  parseParams: (params) => ({
    sessionId: z.string().parse(params.sessionId),
  }),
});

export const countByLocationItemScanRoute = createRoute({
  getParentRoute: () => countByLocationSessionRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./ItemScan/ItemScanView')),
});

const countByLocationItemCountRoute = createRoute({
  getParentRoute: () => countByLocationSessionRoute,
  path: 'item/$itemId',
  component: lazyRouteComponent(() => import('./ItemCount/ItemCountView')),
  parseParams: (params) => ({
    itemId: z.string().parse(params.itemId),
  }),
});

const countByLocationReviewContainerRoute = createRoute({
  getParentRoute: () => countByLocationSessionRoute,
  path: 'review',
});

const countByLocationStockCountReviewRoute = createRoute({
  getParentRoute: () => countByLocationReviewContainerRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./StockCountReview/StockCountReviewView')),
});

export const countByLocationItemReviewRoute = createRoute({
  getParentRoute: () => countByLocationReviewContainerRoute,
  path: '$itemIdForRecount',
  component: lazyRouteComponent(() => import('./StockCountReview/ItemReview/ItemReviewView')),
  parseParams: (params) => ({
    itemIdForRecount: z.string().parse(params.itemIdForRecount),
  }),
});

export const stockCountRouteTree = stockCountMainRoute.addChildren([
  requireWarehouseRoute,
  stockCountEnabledRoute.addChildren([
    stockCountWarehouseWrapperRoute.addChildren([
      stockCountViewRoute,
      countByLocationRoute.addChildren([
        countByLocationSessionRoute.addChildren([
          countByLocationItemScanRoute,
          countByLocationItemCountRoute,
          countByLocationReviewContainerRoute.addChildren([
            countByLocationStockCountReviewRoute,
            countByLocationItemReviewRoute,
          ]),
        ]),
      ]),
    ]),
  ]),
]);
